import { useGlobalStore } from "../providers/GlobalStore";
import { useLmsClient } from "./lms";
import { useViewer } from "../providers/ViewerProvider";
import { useEffect, useState } from "react";
import { ScormDataModel } from "../components/ScormPlayer/ScormDataModel";
import { useAuth0 } from "../react-auth0-wrapper";
import { SCORM_VERSIONS } from "../components/OnlineContent/SCORMPlayer";
import { COURSE_REGISTRATION_DETAILS_QUERY } from "../queries/registrations";
import { Maybe } from "../types/Maybe";
import { setStateType } from "../types/SetStateType";

const useScormSetup = (
  contentId: string,
  entrypoint: string,
  version: string,
  setApolloError: setStateType<any>,
  registrationId?: string,
  childWindow?: Window,
) => {
  const globalStore = useGlobalStore();
  const scormLmsClient = useLmsClient(true);
  const mainLmsClient = useLmsClient(false);
  const { viewer } = useViewer();
  const [scormDataModel, setScormDataModel] =
    useState<Maybe<ScormDataModel>>(null);
  const [scormApiReady, setScormApiReady] = useState(false);
  const [frameSrc, setFrameSrc] = useState<string>("");

  useEffect(() => {
    // update scormDataModel's contentId if contentId changes
    setScormDataModel(
      new ScormDataModel(
        viewer && viewer.isPreviewer ? "preview_mode" : registrationId,
        contentId,
        scormLmsClient,
        () => {
          // noinspection JSIgnoredPromiseFromCall
          mainLmsClient.query({
            query: COURSE_REGISTRATION_DETAILS_QUERY,
            variables: {
              id: registrationId,
            },
            fetchPolicy: "network-only",
          });
        },
        (error: any) => {
          setApolloError(error);
        },
      ),
    );
  }, [
    setApolloError,
    contentId,
    viewer,
    scormLmsClient,
    registrationId,
    mainLmsClient,
  ]);

  const { user } = useAuth0();

  useEffect(() => {
    if (!scormDataModel) return;

    const { ApiType, apiName, userMapper } =
      SCORM_VERSIONS[version as keyof typeof SCORM_VERSIONS];

    setScormApiReady(false);
    setFrameSrc("");

    // Ignore late load/fetch callbacks from a run that has been cleaned up
    let active = true;

    scormDataModel.load(() => {
      if (!active) return;

      (window as any)[apiName] = new ApiType(scormDataModel, {
        cmi: {
          learner_id: user!.sub,
          core: {
            ...userMapper(user),
          },
        },
      });
      setScormApiReady(true);
    });

    const xhttp = new XMLHttpRequest();
    xhttp.onreadystatechange = function () {
      if (active && this.readyState === 4 && this.status === 200) {
        setFrameSrc(
          `/files/${
            JSON.parse(this.responseText).encryptedFilePath
          }/${entrypoint.split("/").slice(1).join("/")}`,
        );
      }
    };
    const fileRoute = viewer?.isPreviewer
      ? `/preview/${globalStore.previewJWT}/content/${contentId}/access`
      : `/registerables/${registrationId}/content/${contentId}/access`;

    xhttp.open("GET", fileRoute, true);
    xhttp.setRequestHeader("Authorization", `Bearer ${globalStore.token}`);
    xhttp.send();

    return () => {
      active = false;
      (window as any)[apiName] = null;
    };
  }, [
    registrationId,
    contentId,
    entrypoint,
    globalStore.token,
    user,
    scormDataModel,
    version,
    viewer,
    globalStore.previewJWT,
  ]);

  // Only navigate the popup once the SCORM API is attached and the content
  // URL is known, otherwise the content loads before it can find the API
  useEffect(() => {
    if (childWindow && scormApiReady && frameSrc) {
      childWindow.location.href = frameSrc;
    }
  }, [childWindow, scormApiReady, frameSrc]);

  return { scormApiReady, frameSrc, scormDataModel };
};

export default useScormSetup;
